(function () {
  var geoCache = {};
  var geoapifyAbortController = null;

  function ready(callback) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback);
    } else {
      callback();
    }
  }

  function normalize(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/&/g, " and ")
      .replace(/\bmt\b\.?/g, " mount ")
      .replace(/\bst\b\.?/g, " saint ")
      .replace(/\bft\b\.?/g, " fort ")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function compact(value) {
    return normalize(value).replace(/\s+/g, "");
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function readJsonScript(root, selector) {
    var node = root.querySelector(selector) || document.querySelector(selector);

    if (!node) return [];

    try {
      return JSON.parse(node.textContent || "[]");
    } catch (error) {
      return [];
    }
  }

  function getGeoapifyUrl(query, apiKey) {
    return (
      "https://api.geoapify.com/v1/geocode/search?text=" +
      encodeURIComponent(query || "") +
      "&limit=5&format=geojson&apiKey=" +
      encodeURIComponent(apiKey || "")
    );
  }

  function extractGeoapifyCandidates(data, toNumber) {
    return ((data && data.features) || [])
      .map(function (feature) {
        var properties = feature && feature.properties ? feature.properties : {};
        var coordinates =
          feature &&
          feature.geometry &&
          Array.isArray(feature.geometry.coordinates)
            ? feature.geometry.coordinates
            : [];
        var lat = toNumber(coordinates[1]);
        var lng = toNumber(coordinates[0]);

        if (!feature || lat === null || lng === null) return null;

        return {
          name:
            properties.city ||
            properties.town ||
            properties.village ||
            properties.county ||
            properties.name ||
            properties.formatted ||
            "",
          country: properties.country || properties.country_code || "",
          state: properties.state || "",
          stateCode: properties.state_code || "",
          lat: lat,
          lng: lng,
        };
      })
      .filter(Boolean);
  }

  function resolveGeoLocation(query, apiKey, toNumber) {
    var cacheKey = "events_search_geoapify_v1_" + compact(query);
    var stored;

    if (!apiKey || !String(query || "").trim()) return Promise.resolve(null);

    if (Object.prototype.hasOwnProperty.call(geoCache, cacheKey)) {
      return Promise.resolve(geoCache[cacheKey]);
    }

    try {
      stored = JSON.parse(sessionStorage.getItem(cacheKey) || "null");
      if (stored && Date.now() - stored.timestamp < 1000 * 60 * 60) {
        geoCache[cacheKey] = stored.value;
        return Promise.resolve(stored.value);
      }
    } catch (error) {
      // Ignore storage failures.
    }

    if (geoapifyAbortController) geoapifyAbortController.abort();
    geoapifyAbortController =
      "AbortController" in window ? new AbortController() : null;

    return fetch(getGeoapifyUrl(query, apiKey), {
      signal: geoapifyAbortController
        ? geoapifyAbortController.signal
        : undefined,
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Geoapify request failed");
        return response.json();
      })
      .then(function (data) {
        var value = extractGeoapifyCandidates(data, toNumber)[0] || null;

        geoCache[cacheKey] = value;

        try {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              timestamp: Date.now(),
              value: value,
            }),
          );
        } catch (error) {
          // Ignore storage failures.
        }

        return value;
      })
      .catch(function (error) {
        if (error && error.name === "AbortError") return null;
        return null;
      });
  }

  function dedupeByUrl(items) {
    var seen = {};

    return items.filter(function (item) {
      var key = normalizeUrlKey(item.url || item.cityUrl || item.title);

      if (!key || seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function normalizeUrlKey(value) {
    var url = String(value || "");

    try {
      url = new URL(url, window.location.origin).pathname;
    } catch (error) {
      // Fall back to text normalization below.
    }

    return normalize(url).replace(/\bpage\b|\bpages\b|\bblogs\b|\bevents\b/g, "").trim();
  }

  function isBroadEventQuery(query) {
    var normalizedQuery = normalize(query);
    var broadTerms = [
      "christmas",
      "holiday",
      "holidays",
      "event",
      "events",
      "market",
      "markets",
      "light",
      "lights",
      "display",
      "displays",
      "tree",
      "trees",
      "festival",
      "festivals",
      "parade",
      "parades",
      "show",
      "shows",
      "family",
      "activity",
      "activities",
      "winter",
      "wonderland",
    ];

    if (!normalizedQuery) return false;

    return broadTerms.some(function (term) {
      return normalizedQuery === term || normalizedQuery.indexOf(term + " ") !== -1 || normalizedQuery.indexOf(" " + term) !== -1;
    });
  }

  function looksLikeZip(query) {
    return /^\d{5}(?:-\d{4})?$/.test(String(query || "").trim());
  }

  function splitList(value) {
    if (Array.isArray(value)) {
      return value
        .reduce(function (items, item) {
          return items.concat(splitList(item));
        }, [])
        .filter(Boolean);
    }

    return String(value || "")
      .split(/[,;\n|â€¢Â·]+/)
      .map(function (item) {
        return item.trim();
      })
      .filter(Boolean);
  }

  function uniqueDisplayParts(parts) {
    var accepted = [];

    parts.forEach(function (part) {
      var key = normalize(part);

      if (!key) return;
      if (
        accepted.some(function (acceptedPart) {
          var acceptedKey = normalize(acceptedPart);

          return (
            acceptedKey === key ||
            acceptedKey.indexOf(key) !== -1 ||
            key.indexOf(acceptedKey) !== -1
          );
        })
      ) {
        return;
      }

      accepted.push(part);
    });

    return accepted;
  }

  function parseEvents(rawEvents, normalizeUrl, toNumber) {
    return (rawEvents || [])
      .map(function (event) {
        var categories = splitList(event.category);

        return {
          title: event.title || "",
          url: normalizeUrl(event.url || ""),
          location: uniqueDisplayParts([
            event.location,
            event.city,
            event.stateCode || event.state,
          ]).join(", "),
          categories: categories,
          categoryText: categories.join(" "),
          image: event.image || "",
          city: event.city || "",
          state: event.state || "",
          stateCode: event.stateCode || "",
          zip: event.zip || "",
          lat: toNumber(event.lat),
          lng: toNumber(event.lng),
        };
      })
      .filter(function (event) {
        return !!(event.title && event.url);
      });
  }

  function listContains(items, normalizedValue) {
    return (items || []).some(function (item) {
      return normalize(item) === normalizedValue;
    });
  }

  function scoreMapping(query, mapping, getStateQuery) {
    var normalizedQuery = normalize(query);
    var compactQuery = compact(query);
    var queryWords = normalizedQuery.split(" ").filter(Boolean);
    var stateQuery = getStateQuery(query);
    var aliases = mapping.searchAliases || [];
    var zips = mapping.zipCodes || [];
    var neighborhoods = mapping.neighborhoods || [];
    var searchCity = normalize(mapping.searchCity);
    var displayName = normalize(mapping.displayName);
    var parentTitle = normalize(mapping.parentCityPageTitle);
    var terms = [
      mapping.searchCity,
      mapping.displayName,
      mapping.parentCityPageTitle,
      mapping.state,
      mapping.stateCode,
    ]
      .concat(aliases)
      .concat(zips)
      .concat(neighborhoods)
      .filter(Boolean);
    var combinedTerms = terms.map(normalize).join(" ");
    var score = 0;

    if (!normalizedQuery) return 0;

    if (listContains(aliases, normalizedQuery)) score = Math.max(score, 500);
    if (listContains(zips, normalizedQuery)) score = Math.max(score, 480);
    if (searchCity === normalizedQuery || displayName === normalizedQuery) {
      score = Math.max(score, 460);
    }
    if (listContains(neighborhoods, normalizedQuery)) {
      score = Math.max(score, 420);
    }
    if (parentTitle === normalizedQuery) score = Math.max(score, 380);

    terms.forEach(function (term) {
      var normalizedTerm = normalize(term);

      if (!normalizedTerm) return;

      if (compactQuery === compact(term)) {
        score = Math.max(score, 300);
      } else if (
        normalizedQuery.length >= 3 &&
        normalizedTerm.indexOf(normalizedQuery) === 0
      ) {
        score = Math.max(score, 240);
      } else if (
        normalizedQuery.length >= 3 &&
        normalizedTerm.indexOf(normalizedQuery) !== -1
      ) {
        score = Math.max(score, 120);
      }
    });

    if (
      queryWords.length > 1 &&
      queryWords.every(function (word) {
        return combinedTerms.indexOf(word) !== -1;
      })
    ) {
      score = Math.max(score, 220 + queryWords.length);
    }

    if (
      stateQuery &&
      (normalize(mapping.state) === stateQuery ||
        normalize(mapping.stateCode) === normalize(query))
    ) {
      score = Math.max(score, 160);
    }

    if (mapping.isHubCity && score > 0) score += 5;

    return score;
  }

  function scoreEvent(query, event) {
    var normalizedQuery = normalize(query);
    var text = normalize(
      [
        event.title,
        event.location,
        event.city,
        event.state,
        event.stateCode,
        event.zip,
        event.categoryText,
      ].join(" "),
    );
    var score = 0;

    if (!normalizedQuery) return 0;
    if (normalize(event.title) === normalizedQuery) score = Math.max(score, 260);
    if (normalize(event.title).indexOf(normalizedQuery) !== -1) {
      score = Math.max(score, 180);
    }
    if (compact(event.city) === compact(query)) score = Math.max(score, 170);
    if (text.indexOf(normalizedQuery) !== -1) score = Math.max(score, 100);

    return score;
  }

  function mappingToCard(mapping, score, normalizeUrl) {
    var hub = mapping.hub || {};
    var parentTitle = hub.title || hub.cityName || mapping.parentCityPageTitle;
    var title = mapping.displayName || mapping.searchCity || parentTitle;
    var labelParts = [];

    if (parentTitle && normalize(parentTitle) !== normalize(title)) {
      labelParts.push(parentTitle);
    }

    labelParts = labelParts.concat([mapping.state, mapping.stateCode]);

    return {
      title: title,
      url: normalizeUrl(hub.url || hub.cityUrl || mapping.parentCityPageUrl),
      meta: uniqueDisplayParts(labelParts).join(", "),
      score: score || 0,
    };
  }

  function hubToCard(hub, score, normalizeUrl) {
    return {
      title: hub.title || hub.cityName,
      url: normalizeUrl(hub.url || hub.cityUrl),
      meta: uniqueDisplayParts([hub.state, hub.stateCode]).join(", "),
      score: score || 0,
      lat: hub.lat,
      lng: hub.lng,
    };
  }

  function eventToCard(event, score) {
    return {
      title: event.title,
      url: event.url,
      meta: event.location || event.categoryText,
      image: event.image,
      score: score || 0,
      lat: event.lat,
      lng: event.lng,
    };
  }

  function renderLocationCard(location) {
    return (
      '<a class="events-search-card events-search-card--location" href="' +
      escapeHtml(location.url) +
      '">' +
      '<span class="events-search-card__placeholder" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" focusable="false"><path d="M12 12.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M18.5 10c0 5.4-6.5 11-6.5 11S5.5 15.4 5.5 10a6.5 6.5 0 1 1 13 0Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>' +
      "</span>" +
      '<span class="events-search-card__content">' +
      '<span class="events-search-card__type">Location</span>' +
      '<h3 class="events-search-card__title">' +
      escapeHtml(location.title) +
      "</h3>" +
      (location.meta
        ? '<span class="events-search-card__meta">' +
          escapeHtml(location.meta) +
          "</span>"
        : "") +
      "</span></a>"
    );
  }

  function renderEventCard(event) {
    var media = event.image
      ? '<span class="events-search-card__image-wrap"><img class="events-search-card__image" src="' +
        escapeHtml(event.image) +
        '" alt="" loading="lazy"></span>'
      : '<span class="events-search-card__placeholder" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M7 4v3M17 4v3M5.5 8.5h13M6 6h12a1.5 1.5 0 0 1 1.5 1.5V19A1.5 1.5 0 0 1 18 20.5H6A1.5 1.5 0 0 1 4.5 19V7.5A1.5 1.5 0 0 1 6 6Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg></span>';

    return (
      '<a class="events-search-card events-search-card--event" href="' +
      escapeHtml(event.url) +
      '">' +
      media +
      '<span class="events-search-card__content">' +
      '<span class="events-search-card__type">Event</span>' +
      '<h3 class="events-search-card__title">' +
      escapeHtml(event.title) +
      "</h3>" +
      (event.meta
        ? '<span class="events-search-card__meta">' +
          escapeHtml(event.meta) +
          "</span>"
        : "") +
      "</span></a>"
    );
  }

  function renderGroup(group, grid, cards, renderer) {
    if (!group || !grid) return;

    if (!cards.length) {
      group.hidden = true;
      grid.innerHTML = "";
      return;
    }

    grid.innerHTML = cards.map(renderer).join("");
    group.hidden = false;
  }

  function initEventsSearch(root) {
    var input = root.querySelector("[data-events-search-input]");
    var form = input ? input.closest("form") : null;
    var locationGroup = root.querySelector(
      "[data-events-search-dynamic-location-group]",
    );
    var locationGrid = root.querySelector(
      "[data-events-search-dynamic-location-grid]",
    );
    var eventGroup = root.querySelector("[data-events-search-dynamic-event-group]");
    var eventGrid = root.querySelector("[data-events-search-dynamic-event-grid]");
    var emptyState = root.querySelector("[data-events-search-empty]");
    var query = root.dataset.eventsSearchTerms || (input && input.value) || "";
    var geoapifyApiKey = root.dataset.geoapifyApiKey || "";
    var searchUtils = window.CityGuideSearchUtils || {};
    var normalizeUrl =
      searchUtils.normalizeUrl ||
      function (url) {
        return String(url || "");
      };
    var toNumber =
      searchUtils.toNumber ||
      function (value) {
        var number = parseFloat(value);
        return Number.isFinite(number) ? number : null;
      };
    var calculateDistanceMiles =
      searchUtils.calculateDistanceMiles ||
      function () {
        return Number.POSITIVE_INFINITY;
      };
    var getStateQuery =
      searchUtils.getStateQuery ||
      function () {
        return "";
      };
    var cityHubs = searchUtils.parseCityHubs
      ? searchUtils.parseCityHubs(
          readJsonScript(root, "[data-events-search-city-hubs]"),
        )
      : [];
    var locationMappings = searchUtils.parseLocationMappings
      ? searchUtils.parseLocationMappings(
          readJsonScript(root, "[data-events-search-location-mapping-index]"),
          cityHubs,
        )
      : [];
    var eventIndex = parseEvents(
      readJsonScript(root, "[data-events-search-event-index]"),
      normalizeUrl,
      toNumber,
    );

    function getExistingResultUrls() {
      var urls = {};

      Array.prototype.slice
        .call(root.querySelectorAll(".events-search-card[href]"))
        .forEach(function (card) {
          if (
            (locationGrid && locationGrid.contains(card)) ||
            (eventGrid && eventGrid.contains(card))
          ) {
            return;
          }

          urls[normalizeUrlKey(card.getAttribute("href"))] = true;
        });

      return urls;
    }

    function removeExistingUrls(cards, existingUrls) {
      return cards.filter(function (card) {
        return !existingUrls[normalizeUrlKey(card.url)];
      });
    }

    function isLocationLikeSearch(searchQuery, point) {
      var normalizedQuery = normalize(searchQuery);
      var words = normalize(searchQuery).split(" ").filter(Boolean);
      var hasLocationMention = locationMappings.concat(cityHubs).some(function (item) {
        var locationName = normalize(
          item.searchCity ||
            item.displayName ||
            item.parentCityPageTitle ||
            item.title ||
            item.cityName,
        );

        return locationName && normalizedQuery.indexOf(locationName) !== -1;
      });

      if (!searchQuery) return false;
      if (looksLikeZip(searchQuery)) return true;
      if (point && point.score >= 150) return true;
      if (getStateQuery(searchQuery)) return true;
      if (hasLocationMention) return true;
      if (isBroadEventQuery(searchQuery)) return false;

      return words.length > 0 && words.length <= 3;
    }

    function getBestQueryPoint(searchQuery) {
      var mappingMatch = locationMappings
        .map(function (mapping) {
          return {
            mapping: mapping,
            score: scoreMapping(searchQuery, mapping, getStateQuery),
          };
        })
        .filter(function (match) {
          return match.score > 0;
        })
        .sort(function (a, b) {
          return b.score - a.score;
        })[0];
      var hubMatch = cityHubs
        .map(function (hub) {
          var hubText = normalize(
            [hub.title, hub.cityName, hub.state, hub.stateCode].join(" "),
          );
          var score = 0;

          if (compact(hub.title || hub.cityName) === compact(searchQuery)) {
            score = 360;
          } else if (hubText.indexOf(normalize(searchQuery)) === 0) {
            score = 220;
          } else if (
            getStateQuery(searchQuery) &&
            normalize(hub.state) === getStateQuery(searchQuery)
          ) {
            score = 150;
          }

          return {
            hub: hub,
            score: score,
          };
        })
        .filter(function (match) {
          return match.score > 0;
        })
        .sort(function (a, b) {
          return b.score - a.score;
        })[0];

      if (mappingMatch && (!hubMatch || mappingMatch.score >= hubMatch.score)) {
        return {
          title:
            mappingMatch.mapping.displayName ||
            mappingMatch.mapping.searchCity ||
            mappingMatch.mapping.parentCityPageTitle,
          lat: mappingMatch.mapping.lat,
          lng: mappingMatch.mapping.lng,
          score: mappingMatch.score,
        };
      }

      if (hubMatch) {
        return {
          title: hubMatch.hub.title || hubMatch.hub.cityName,
          lat: hubMatch.hub.lat,
          lng: hubMatch.hub.lng,
          score: hubMatch.score,
        };
      }

      return null;
    }

    function getLocationMatches(searchQuery, point, limit) {
      var mappingCards = locationMappings
        .map(function (mapping) {
          return mappingToCard(
            mapping,
            scoreMapping(searchQuery, mapping, getStateQuery),
            normalizeUrl,
          );
        })
        .filter(function (card) {
          return card.score > 0 && card.title && card.url;
        });
      var hubCards = cityHubs
        .map(function (hub) {
          var card = hubToCard(hub, 0, normalizeUrl);
          var hubText = normalize(
            [hub.title, hub.cityName, hub.state, hub.stateCode].join(" "),
          );

          if (compact(hub.title || hub.cityName) === compact(searchQuery)) {
            card.score = Math.max(card.score, 360);
          } else if (hubText.indexOf(normalize(searchQuery)) === 0) {
            card.score = Math.max(card.score, 220);
          }

          if (point && hub.lat !== null && hub.lng !== null) {
            card.distance = calculateDistanceMiles(
              point.lat,
              point.lng,
              hub.lat,
              hub.lng,
            );
            if (card.distance <= 250) {
              card.score = Math.max(
                card.score,
                180 - Math.min(Math.round(card.distance), 160),
              );
            }
          }

          return card;
        })
        .filter(function (card) {
          return card.score > 0 && card.title && card.url;
        });

      return dedupeByUrl(
        mappingCards.concat(hubCards).sort(function (a, b) {
          if (b.score !== a.score) return b.score - a.score;
          return normalize(a.title).localeCompare(normalize(b.title));
        }),
      ).slice(0, limit || 8);
    }

    function getEventMatches(searchQuery, point, limit) {
      return eventIndex
        .map(function (event) {
          var score = scoreEvent(searchQuery, event);
          var card = eventToCard(event, score);

          if (point && event.lat !== null && event.lng !== null) {
            card.distance = calculateDistanceMiles(
              point.lat,
              point.lng,
              event.lat,
              event.lng,
            );
            if (card.distance <= 150) {
              card.score = Math.max(
                card.score,
                180 - Math.min(Math.round(card.distance), 140),
              );
            }
          }

          return card;
        })
        .filter(function (card) {
          return card.score > 0 && card.title && card.url;
        })
        .sort(function (a, b) {
          if (b.score !== a.score) return b.score - a.score;
          return normalize(a.title).localeCompare(normalize(b.title));
        })
        .slice(0, limit || 8);
    }

    function renderSearchSuggestions(searchQuery, point) {
      var locations;
      var events;
      var existingUrls = getExistingResultUrls();
      var allowNearby = isLocationLikeSearch(searchQuery, point);

      if (!searchQuery && !point) return Promise.resolve();

      if (!allowNearby) {
        renderGroup(locationGroup, locationGrid, [], renderLocationCard);
        renderGroup(eventGroup, eventGrid, [], renderEventCard);
        return Promise.resolve({
          locations: [],
          events: [],
        });
      }

      locations = removeExistingUrls(
        getLocationMatches(searchQuery, point, 8),
        existingUrls,
      );
      locations.forEach(function (location) {
        existingUrls[normalizeUrlKey(location.url)] = true;
      });
      events = removeExistingUrls(
        getEventMatches(searchQuery, point, 8),
        existingUrls,
      );

      renderGroup(
        locationGroup,
        locationGrid,
        locations,
        renderLocationCard,
      );
      renderGroup(
        eventGroup,
        eventGrid,
        events,
        renderEventCard,
      );

      if (emptyState) {
        emptyState.hidden = locations.length > 0 || events.length > 0;
      }

      if (
        point ||
        locations.length ||
        normalize(searchQuery).length < 2 ||
        !geoapifyApiKey ||
        isBroadEventQuery(searchQuery)
      ) {
        return Promise.resolve({
          locations: locations,
          events: events,
        });
      }

      return resolveGeoLocation(searchQuery, geoapifyApiKey, toNumber).then(
        function (geoLocation) {
          if (!geoLocation) {
            return {
              locations: locations,
              events: events,
            };
          }

          locations = getLocationMatches(searchQuery, geoLocation, 8);
          locations = removeExistingUrls(locations, existingUrls);
          locations.forEach(function (location) {
            existingUrls[normalizeUrlKey(location.url)] = true;
          });
          events = removeExistingUrls(
            getEventMatches(searchQuery, geoLocation, 8),
            existingUrls,
          );

          renderGroup(locationGroup, locationGrid, locations, renderLocationCard);
          renderGroup(eventGroup, eventGrid, events, renderEventCard);

          if (emptyState) {
            emptyState.hidden = locations.length > 0 || events.length > 0;
          }

          return {
            locations: locations,
            events: events,
          };
        },
      );
    }

    if (form && input) {
      form.addEventListener("submit", function (event) {
        if (!input.value.trim()) {
          event.preventDefault();
          input.focus();
        }
      });
    }

    if (query) {
      renderSearchSuggestions(query, getBestQueryPoint(query));
    }
  }

  ready(function () {
    Array.prototype.slice
      .call(document.querySelectorAll("[data-section-type='events-search']"))
      .forEach(initEventsSearch);
  });
})();
